import "./legacy";
import "@ui/bw.css";
import "@ui/layouts.css";
import "./style.css";
import { createRouter } from "./router";
import { titleView } from "./views/title";
import { menuView } from "./views/menu";
import { lobbyView } from "./views/lobby";
import { matchView } from "./views/match";
import { loginView, registerView, forgotView, resetView, verifyEmailView } from "./views/auth";
import { deckView } from "./views/deck";
import { rankingView } from "./views/ranking";
import { collectionView } from "./views/collection";
import { applyTheme, loadTheme, toggleTheme } from "./theme";
import { currentUser, loadSession } from "./session";
import { installTooltips } from "./tooltip";
import { audioPrefs, installAudioUnlock, play, setAudioPrefs } from "./audio/sfx";
import { creditsView } from "./views/credits";
import { devCheckoutView, shopView } from "./views/shop";
import { marketView } from "./views/market";
import { changelogView } from "./views/changelog";
import { crecheView } from "./views/creche";
import { missionsView } from "./views/missions";
import { achievementsView } from "./views/achievements";
import { moderationView } from "./views/moderation";
import { tutorialView } from "./views/tutorial";
import { loadBlocks, openReport } from "./report";
import { loadFeatures } from "./features";
import { installSocial } from "./social";
import { applyDocumentLang, setLang, t, type Lang } from "./i18n";

applyTheme(loadTheme());
applyDocumentLang();
installTooltips();
installAudioUnlock();

// troca de idioma em qualquer tela: redesenha a rota atual
document.addEventListener("change", (e) => {
  const sel = (e.target as HTMLElement).closest<HTMLSelectElement>("[data-lang-select]");
  if (!sel) return;
  setLang(sel.value as Lang);
  window.dispatchEvent(new HashChangeEvent("hashchange"));
});

// botão ⚑ (denunciar/bloquear) em qualquer tela; quem desenha o botão pode
// pôr o chat da sala e a partida no evento "pokechess:report-context"
document.addEventListener("click", (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-report]");
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const ctx: { matchId?: string; roomChat?: { nickname: string; text: string; at: number }[] } = {};
  window.dispatchEvent(new CustomEvent("pokechess:report-context", { detail: ctx }));
  void openReport({ id: btn.dataset.report!, nickname: btn.dataset.reportName ?? "", ...ctx });
}, true);

// botão B/W em qualquer tela
document.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).closest("[data-theme-toggle]")) toggleTheme();
  if ((e.target as HTMLElement).closest("[data-sound-toggle]")) {
    const on = !audioPrefs().sound;
    setAudioPrefs({ sound: on, vibrate: on });
    document.querySelectorAll<HTMLElement>("[data-sound-toggle]").forEach((b) => {
      b.textContent = on ? "🔊" : "🔇";
      b.setAttribute("aria-pressed", String(on));
    });
    if (on) play("click");
  }
});

const root = document.getElementById("app")!;
root.tabIndex = -1;
root.innerHTML = `<main class="shell-center"><img class="brand-logo brand-logo--splash" src="/icons/logo-256.png" alt="" width="96" height="96"><p class="bw-dim">${t("common.loading")}</p></main>`;

await Promise.all([loadSession(), loadFeatures()]);
createRouter(
  root,
  {
    "/titulo": { view: titleView },
    "/entrar": { view: loginView, guest: true },
    "/cadastro": { view: registerView, guest: true },
    "/recuperar-senha": { view: forgotView },
    "/nova-senha": { view: resetView },
    "/verificar-email": { view: verifyEmailView },
    "/menu": { view: menuView, auth: true },
    "/fila": { view: lobbyView, auth: true },
    "/partida": { view: matchView, auth: true },
    "/deck": { view: deckView, auth: true },
    "/ranking": { view: rankingView, auth: true },
    "/colecao": { view: collectionView, auth: true },
    "/creditos": { view: creditsView },
    "/loja": { view: shopView, auth: true },
    "/mercado": { view: marketView, auth: true },
    "/novidades": { view: changelogView },
    "/criadouro": { view: crecheView, auth: true },
    "/missoes": { view: missionsView, auth: true },
    "/conquistas": { view: achievementsView, auth: true },
    "/moderacao": { view: moderationView, auth: true },
    "/tutorial": { view: tutorialView, auth: true },
    "/loja/checkout-teste": { view: devCheckoutView, auth: true },
  },
  "/titulo",
  () => currentUser() !== null,
);

installSocial();
if (currentUser()) void loadBlocks();

if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* sem SW: o jogo funciona online normalmente */
    });
  });
}

// Só em desenvolvimento: ganchos para os roteiros de gravação de vídeo (media/) e testes.
if (import.meta.env.DEV) {
  void import("./game/store").then(({ gameStore }) => {
    (window as unknown as { __pokechess: unknown }).__pokechess = { gameStore };
  });
}
